import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatTL, formatDate } from "../lib/format";
import { MALIYET_KATEGORILERI, BOLUMLER } from "../lib/sabitler";
import { MaliyetForm } from "../components/MaliyetForm";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Search,
  PlusCircle,
  ListFilter,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Package,
  Target,
  PiggyBank,
  Pencil,
} from "lucide-react";

const PAGE_SIZE = 50;
const HEPSI = "__hepsi__";
const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const FiltreSelect = ({ label, value, onChange, options, testid }) => (
  <div className="space-y-1">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <Select value={value || HEPSI} onValueChange={(v) => onChange(v === HEPSI ? "" : v)}>
      <SelectTrigger className="h-10" data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-card">
        <SelectItem value={HEPSI}>Tümü</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

export default function MaliyetPage() {
  const [tab, setTab] = useState("yeni");
  const [formKey, setFormKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [ozetSignal, setOzetSignal] = useState(0);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ baslangic: "", bitis: "", kategori: "", bolum: "" });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef(null);

  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE };
      Object.entries(filters).forEach(([k, v]) => v && (params[k] = v));
      if (debouncedSearch) params.q = debouncedSearch;
      const res = await api.get("/maliyet", { params });
      setData(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Kayıtlar yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [page, filters, debouncedSearch]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const setFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      await api.post("/maliyet", payload);
      toast.success("Maliyet kaydı eklendi.");
      setFormKey((k) => k + 1);
      setOzetSignal((s) => s + 1);
      fetchList();
    } catch (err) {
      toast.error(getErrMsg(err, "Kayıt eklenemedi."));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (payload) => {
    setSaving(true);
    try {
      await api.put(`/maliyet/${editTarget.id}`, payload);
      toast.success("Kayıt güncellendi.");
      setEditTarget(null);
      setOzetSignal((s) => s + 1);
      fetchList();
    } catch (err) {
      toast.error(getErrMsg(err, "Kayıt güncellenemedi."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/maliyet/${deleteTarget.id}`);
      toast.success("Kayıt silindi.");
      setDeleteTarget(null);
      setEditTarget(null);
      fetchList();
    } catch (err) {
      toast.error(getErrMsg(err, "Kayıt silinemedi."));
    }
  };

  const kayitlar = data?.kayitlar || [];
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((data?.toplam_kayit || 0) / PAGE_SIZE)),
    [data]
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="space-y-4"
    >
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-12 bg-secondary">
          <TabsTrigger value="yeni" className="h-10 gap-1.5 px-4" data-testid="maliyet-tab-yeni">
            <PlusCircle className="h-4 w-4" />
            Yeni Kayıt
          </TabsTrigger>
          <TabsTrigger value="liste" className="h-10 gap-1.5 px-4" data-testid="maliyet-tab-liste">
            <ListFilter className="h-4 w-4" />
            Kayıtlar {data ? `(${data.toplam_kayit.toLocaleString("tr-TR")})` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="yeni" className="mt-4">
          <div className="rounded-xl border bg-card p-4 sm:p-6 shadow-[0_1px_0_rgba(14,27,46,0.06),0_10px_30px_rgba(14,27,46,0.06)] max-w-3xl">
            <h2 className="font-display text-lg font-semibold mb-1">Maliyet Girişi (SMM)</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Dosya bazlı satılan hizmet maliyeti — KDV hariç matrah üzerinden raporlanır.
            </p>
            <MaliyetForm key={formKey} onSubmit={handleCreate} submitting={saving} />
          </div>
        </TabsContent>

        <TabsContent value="liste" className="mt-4 space-y-4">
          {/* Özet paneli */}
          <SmmOzetPanel ozetSignal={ozetSignal} aktif={tab === "liste"} />

          {/* Filtreler */}
          <div className="rounded-xl border bg-card p-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Başlangıç</Label>
                <Input
                  type="date"
                  value={filters.baslangic}
                  onChange={(e) => setFilter("baslangic", e.target.value)}
                  className="h-10"
                  data-testid="maliyet-filter-baslangic"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Bitiş</Label>
                <Input
                  type="date"
                  value={filters.bitis}
                  onChange={(e) => setFilter("bitis", e.target.value)}
                  className="h-10"
                  data-testid="maliyet-filter-bitis"
                />
              </div>
              <FiltreSelect label="Kategori" value={filters.kategori} onChange={(v) => setFilter("kategori", v)} options={MALIYET_KATEGORILERI} testid="maliyet-filter-kategori" />
              <FiltreSelect label="Bölüm" value={filters.bolum} onChange={(v) => setFilter("bolum", v)} options={BOLUMLER} testid="maliyet-filter-bolum" />
              <div className="space-y-1 col-span-2 sm:col-span-1">
                <Label className="text-xs text-muted-foreground">Ara</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Plaka, tedarikçi, belge…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-10 pl-8"
                    data-testid="maliyet-filter-search"
                  />
                </div>
              </div>
            </div>
          </div>

          {loading && !data ? (
            <div className="space-y-2">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <>
              {/* Desktop tablo */}
              <div className="hidden md:block rounded-xl border bg-card overflow-hidden">
                <div className="w-full overflow-x-auto">
                  <table className="w-full text-sm" data-testid="maliyet-table">
                    <thead className="bg-muted/50">
                      <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-3">Tarih</th>
                        <th className="px-3 py-3">Plaka / Dosya No</th>
                        <th className="px-3 py-3">Kategori</th>
                        <th className="px-3 py-3">Bölüm</th>
                        <th className="px-3 py-3">Tedarikçi</th>
                        <th className="px-3 py-3 text-right">Matrah</th>
                        <th className="px-3 py-3 text-right">KDV</th>
                        <th className="px-3 py-3 text-right">Toplam</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kayitlar.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-3 py-10 text-center text-muted-foreground" data-testid="maliyet-empty-message">
                            Filtrelere uyan kayıt bulunamadı.
                          </td>
                        </tr>
                      ) : (
                        kayitlar.map((k) => (
                          <tr
                            key={k.id}
                            className="border-t border-border/60 hover:bg-[hsl(var(--primary))]/[0.03] cursor-pointer transition-colors"
                            onClick={() => setEditTarget(k)}
                            data-testid="maliyet-table-row"
                          >
                            <td className="px-3 py-2.5 whitespace-nowrap tabular-nums">{formatDate(k.tarih)}</td>
                            <td className="px-3 py-2.5 whitespace-nowrap">{k.plaka_dosya_no || <span className="text-muted-foreground">—</span>}</td>
                            <td className="px-3 py-2.5 whitespace-nowrap"><Badge variant="secondary">{k.kategori}</Badge></td>
                            <td className="px-3 py-2.5 whitespace-nowrap">{k.bolum || <span className="text-muted-foreground italic text-xs">Belirtilmemiş</span>}</td>
                            <td className="px-3 py-2.5 max-w-[180px] truncate" title={k.tedarikci}>{k.tedarikci || "—"}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL(k.matrah, 2)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL(k.kdv, 2)}</td>
                            <td className="px-3 py-2.5 text-right font-medium tabular-nums whitespace-nowrap">{formatTL(k.toplam, 2)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobil kartlar */}
              <div className="md:hidden space-y-2" data-testid="maliyet-mobile-list">
                {kayitlar.length === 0 ? (
                  <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
                    Filtrelere uyan kayıt bulunamadı.
                  </div>
                ) : (
                  kayitlar.map((k) => (
                    <div
                      key={k.id}
                      className="rounded-xl border bg-card p-3.5"
                      onClick={() => setEditTarget(k)}
                      role="button"
                      data-testid="maliyet-mobile-card"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground tabular-nums">{formatDate(k.tarih)}</span>
                        <Badge variant="secondary" className="text-[10px]">{k.kategori}</Badge>
                        <span className="ml-auto font-display text-sm font-bold tabular-nums">{formatTL(k.toplam, 2)}</span>
                      </div>
                      <p className="mt-1 truncate text-sm font-medium">{k.plaka_dosya_no || k.tedarikci || "—"}</p>
                      <p className="text-xs text-muted-foreground">{k.bolum || "Belirtilmemiş"} {k.tedarikci ? `• ${k.tedarikci}` : ""}</p>
                    </div>
                  ))
                )}
              </div>

              {/* Toplam satırı */}
              {data && (
                <div
                  className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-xl border bg-card px-4 py-3 text-sm"
                  data-testid="maliyet-totals-bar"
                >
                  <span className="text-muted-foreground">
                    <strong className="text-foreground tabular-nums">{data.toplamlar.adet.toLocaleString("tr-TR")}</strong> kayıt
                  </span>
                  <span className="text-muted-foreground">
                    Matrah: <strong className="text-foreground tabular-nums" data-testid="maliyet-total-matrah">{formatTL(data.toplamlar.matrah, 2)}</strong>
                  </span>
                  <span className="text-muted-foreground">
                    KDV: <strong className="text-foreground tabular-nums">{formatTL(data.toplamlar.kdv, 2)}</strong>
                  </span>
                  <span className="text-muted-foreground">
                    Toplam: <strong className="text-foreground tabular-nums">{formatTL(data.toplamlar.toplam, 2)}</strong>
                  </span>
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex items-center justify-between" data-testid="maliyet-pagination">
                  <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} className="min-h-[44px] gap-1" data-testid="maliyet-pagination-prev">
                    <ChevronLeft className="h-4 w-4" />
                    Önceki
                  </Button>
                  <span className="text-xs text-muted-foreground tabular-nums">Sayfa {page} / {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)} className="min-h-[44px] gap-1" data-testid="maliyet-pagination-next">
                    Sonraki
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Düzenleme dialogu */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="bg-card max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Maliyet Kaydını Düzenle</DialogTitle>
            <DialogDescription>
              {editTarget && `${formatDate(editTarget.tarih)} • ${formatTL(editTarget.toplam, 2)}`}
            </DialogDescription>
          </DialogHeader>
          {editTarget && (
            <MaliyetForm
              key={editTarget.id}
              initial={editTarget}
              onSubmit={handleUpdate}
              submitting={saving}
              submitLabel="Güncelle"
            />
          )}
          <div className="flex justify-end">
            <Button
              variant="ghost"
              onClick={() => setDeleteTarget(editTarget)}
              className="min-h-[44px] gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10"
              data-testid="maliyet-edit-delete-button"
            >
              <Trash2 className="h-4 w-4" />
              Kaydı Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Kaydı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              Bu maliyet kaydı kalıcı olarak silinecek. Devam etmek istiyor musunuz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="min-h-[44px]">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="min-h-[44px] bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="maliyet-delete-confirm-button"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}

/* ================= ÖZET PANELİ (Aylık SMM + Kırılımlar + Bütçe) ================= */

function SmmOzetPanel({ ozetSignal, aktif }) {
  const { user } = useAuth();
  const isYonetici = user.role === "yonetici";
  const suAnkiAy = new Date().getMonth() + 1;
  const [ay, setAy] = useState(String(suAnkiAy));
  const [ozet, setOzet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [butceOpen, setButceOpen] = useState(false);
  const [butceValues, setButceValues] = useState(Array(12).fill(""));
  const [butceSaving, setButceSaving] = useState(false);

  const fetchOzet = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/maliyet/ozet", { params: { yil: 2026, ay: parseInt(ay) } });
      setOzet(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Özet yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [ay]);

  useEffect(() => {
    if (aktif) fetchOzet();
  }, [aktif, fetchOzet, ozetSignal]);

  const openButceDialog = async () => {
    try {
      const res = await api.get("/smm-butce");
      const map = {};
      res.data.forEach((b) => {
        if (b.yil === 2026) map[b.ay] = b.tutar;
      });
      setButceValues(AYLAR.map((_, i) => (map[i + 1] != null ? String(map[i + 1]) : "")));
      setButceOpen(true);
    } catch (err) {
      toast.error(getErrMsg(err, "SMM bütçeleri yüklenemedi."));
    }
  };

  const handleButceKaydet = async () => {
    setButceSaving(true);
    try {
      for (let i = 0; i < 12; i++) {
        const v = butceValues[i];
        if (v === "" || v === null) continue;
        const tutar = parseFloat(v);
        if (isNaN(tutar) || tutar < 0) {
          toast.error(`${AYLAR[i]} için geçersiz tutar.`);
          setButceSaving(false);
          return;
        }
        await api.post("/smm-butce", { yil: 2026, ay: i + 1, tutar });
      }
      toast.success("SMM bütçe hedefleri kaydedildi.");
      setButceOpen(false);
      fetchOzet();
    } catch (err) {
      toast.error(getErrMsg(err, "Bütçe kaydedilemedi."));
    } finally {
      setButceSaving(false);
    }
  };

  if (loading && !ozet) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  }

  if (!ozet) return null;

  const kartlar = [
    { label: `${AYLAR[ozet.ay - 1]} SMM Toplamı`, value: formatTL(ozet.toplam_matrah), icon: Package, cls: "" },
    {
      label: "SMM Bütçesi",
      value: ozet.smm_butce != null ? formatTL(ozet.smm_butce) : "Belirlenmedi",
      icon: Target,
      cls: ozet.smm_butce == null ? "text-muted-foreground text-lg" : "",
    },
    {
      label: "Kalan",
      value: ozet.kalan != null ? formatTL(ozet.kalan) : "—",
      icon: PiggyBank,
      cls: ozet.kalan != null && ozet.kalan < 0 ? "text-red-700" : ozet.kalan != null ? "text-emerald-700" : "text-muted-foreground",
    },
  ];

  return (
    <div className="rounded-xl border bg-card p-4 sm:p-5 space-y-4" data-testid="smm-ozet-panel">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-base font-semibold">Aylık SMM Özeti</h2>
        <div className="ml-auto flex items-center gap-2">
          <Select value={ay} onValueChange={setAy}>
            <SelectTrigger className="h-10 w-36" data-testid="smm-ay-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card">
              {AYLAR.map((a, i) => (
                <SelectItem key={a} value={String(i + 1)}>{a} 2026</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isYonetici && (
            <Button variant="outline" size="sm" onClick={openButceDialog} className="min-h-[40px] gap-1.5" data-testid="smm-butce-duzenle-button">
              <Pencil className="h-3.5 w-3.5" />
              Bütçe Hedefleri
            </Button>
          )}
        </div>
      </div>

      {/* 3 kart */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="smm-ozet-kartlar">
        {kartlar.map((c) => (
          <div key={c.label} className="relative overflow-hidden rounded-lg border bg-card p-3.5">
            <div className="absolute inset-x-0 top-0 h-0.5 bg-[hsl(var(--accent))]/40" />
            <div className="flex items-center gap-2 text-muted-foreground">
              <c.icon className="h-4 w-4" />
              <span className="text-[11px] font-medium uppercase tracking-wide">{c.label}</span>
            </div>
            <p className={`mt-1.5 font-display text-xl font-bold tabular-nums ${c.cls}`}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* Kırılımlar */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div data-testid="smm-kategori-kirilimi">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Kategori Kırılımı</p>
          <div className="space-y-2">
            {ozet.kategori_kirilimi.map((k) => (
              <div key={k.kategori}>
                <div className="flex items-center justify-between text-sm">
                  <span className="truncate">{k.kategori}</span>
                  <span className="tabular-nums text-muted-foreground">
                    <strong className="text-foreground">{formatTL(k.tutar)}</strong> · %{k.yuzde.toLocaleString("tr-TR")}
                  </span>
                </div>
                <div className="mt-1 h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[hsl(var(--accent))] transition-all duration-300"
                    style={{ width: `${Math.min(k.yuzde, 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div data-testid="smm-bolum-kirilimi">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Bölüm Kırılımı</p>
          <div className="space-y-2">
            {ozet.bolum_kirilimi.map((b) => (
              <div key={b.bolum}>
                <div className="flex items-center justify-between text-sm">
                  <span className="truncate">{b.bolum}</span>
                  <span className="tabular-nums text-muted-foreground">
                    <strong className="text-foreground">{formatTL(b.tutar)}</strong> · %{b.yuzde.toLocaleString("tr-TR")}
                  </span>
                </div>
                <div className="mt-1 h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[hsl(var(--primary))]/70 transition-all duration-300"
                    style={{ width: `${Math.min(b.yuzde, 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SMM bütçe hedefleri dialogu (yönetici) */}
      <Dialog open={butceOpen} onOpenChange={setButceOpen}>
        <DialogContent className="bg-card max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">SMM Bütçe Hedefleri (2026)</DialogTitle>
            <DialogDescription>
              Aylık satılan hizmet maliyeti hedeflerini girin. Boş bırakılan aylar değiştirilmez.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {AYLAR.map((a, i) => (
              <div key={a} className="space-y-1">
                <Label className="text-xs">{a}</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  placeholder="—"
                  value={butceValues[i]}
                  onChange={(e) =>
                    setButceValues((arr) => {
                      const next = [...arr];
                      next[i] = e.target.value;
                      return next;
                    })
                  }
                  className="h-10 text-right tabular-nums"
                  data-testid={`smm-butce-ay-${i + 1}-input`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setButceOpen(false)} className="min-h-[44px]">Vazgeç</Button>
            <Button onClick={handleButceKaydet} disabled={butceSaving} className="min-h-[44px]" data-testid="smm-butce-kaydet-button">
              {butceSaving ? "Kaydediliyor…" : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
